import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@framework/ui/atoms/Button';
import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import type { WorkspaceEntity, WorkspaceMemberRoles } from '@workspace/domain/workspace';
import { MemberEditList } from './MemberEditList';
import type { WorkspaceInternalPublicMemberRoleJSON, GroupMemberRolesJSON } from 'balus-common';
import { useMemberFilter } from './useMemberFilter';

type Props = {
  isOpen: boolean;
  onClose(): void;
  workspace: WorkspaceEntity;
  internalPublicSetting: WorkspaceInternalPublicMemberRoleJSON | null;
  memberRoles: WorkspaceMemberRoles;
  groupMemberRoles: GroupMemberRolesJSON;
  currentUserId: string;
  onMemberAdd(): void;
  onMemberRemove(): void;
};

/**
 * 既存のワークスペースメンバーの役割(ロール)を変更するモーダル
 * @returns
 */
export const MemberEditModal: React.FC<Props> = ({
  isOpen,
  onClose,
  workspace,
  internalPublicSetting,
  memberRoles,
  groupMemberRoles,
  currentUserId,
  onMemberAdd,
  onMemberRemove,
}: Props) => {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [MemberSearchPanel, searchPanelProps, memberFilter, resetMemberFilter] = useMemberFilter();

  const handleClose = useCallback(() => {
    setProcessing(false);
    setErrorMessage('');
    resetMemberFilter();

    onClose();
  }, [onClose, resetMemberFilter]);

  const closeButton = (
    <Button color="secondary" onClick={handleClose} disabled={processing}>
      {t('common.action.close')}
    </Button>
  );

  return (
    <GeneralModal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('workspace.memberEdit.memberTitle', { name: workspace.name.value })}
      submitButton={null}
      cancelButton={closeButton}
    >
      <div className="flex justify-end p-2">
        <button
          className="rounded border border-solid border-brand px-2 py-1 text-sm text-brand hover:bg-gray-200"
          onClick={onMemberAdd}
        >
          {t('workspace.individualPermission.addMember')}
        </button>
        <button
          className="ml-2 rounded border border-solid border-red-700 px-2 py-1 text-sm text-red-700 hover:bg-gray-200"
          onClick={onMemberRemove}
        >
          {t('workspace.memberRemove.submitButton')}
        </button>
      </div>

      {internalPublicSetting && (
        <div className="m-2 rounded border border-yellow-700 bg-yellow-200 p-2 text-sm" style={{ width: '600px' }}>
          {t('workspace.memberEdit.internalPublicNote1')}
          <br />
          {t('workspace.memberEdit.internalPublicNote2')}
          <br />
          {t('workspace.memberEdit.internalPublicNote3')}
        </div>
      )}
      <MemberSearchPanel {...searchPanelProps} />
      <div className="mb-4 mt-2 overflow-y-auto p-2" style={{ width: '600px', minHeight: '250px', maxHeight: '50vh' }}>
        <MemberEditList
          workspaceId={workspace.id}
          currentUserId={currentUserId}
          memberRoles={memberRoles}
          groupMemberRoles={groupMemberRoles}
          memberFilter={memberFilter}
        />
      </div>
      {errorMessage && <div className="p-2 text-red-700">{errorMessage}</div>}
    </GeneralModal>
  );
};
